import React, { useState } from 'react';
import clsx from 'clsx';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import useAIStudioMedia from '../../hooks/useAIStudioMedia';
import { useProject } from '../../../context/ProjectContext';
import ImageModal from '../../media-gallery/image-modal/ImageModal';
import { AI_MEDIA_TYPES, AI_STUDIO_ADAPTERS } from '../../../constants';
import useCommonModal from '../../../hooks/useCommonModal';
import Image from './Image';
import Video from './Video';
import CommonLoader from '../../../components/common-loader/CommonLoader';
import CardActions from '../card-actions/CardActions';
import downloadFile from '../../../utils/downloadFile';
import ConfirmationModal from '../../../components/confirmation-modal/ConfirmationModal';
import FailedMedia from '../../components/failed-media/FailedMedia';

import scss from './MediaCard.module.scss';

function MediaCard({ id, sourceExists, additionalCardActions }) {
  const { id: projectId } = useParams();
  const { actions } = useProject();

  const { t } = useTranslation('studio');

  const { projectVariant } = useProject();
  const { mediaUrl, isLoading, mediaType, fileName, error } = useAIStudioMedia({ id, projectVariant });
  const { isOpen, handleOpenModal, handleCloseModal } = useCommonModal({ initialState: false });
  const [mediaToRemove, setMediaToRemove] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState([]);

  const isSaveToBucketAvailable = sourceExists;

  const handleCreateNewBucket = async (id) => {
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.CREATING_NEW_BUCKET]);
    await actions('createNewBucketFromFavorite', { projectId, id });
    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.CREATING_NEW_BUCKET));
  };

  const handleDownload = () => {
    if (mediaUrl) {
      downloadFile(mediaUrl, fileName);
    }
  };

  const handleRemove = (id) => {
    setMediaToRemove(id);
  };

  const handleCloseConfirmationModal = () => {
    setMediaToRemove(null);
  };

  const handleRemoveMedia = async () => {
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.REMOVING_MEDIA]);
    setMediaToRemove(null);
    await actions('removeFavoriteMedia', { id: mediaToRemove, projectId });
    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.REMOVING_MEDIA));
  };

  const handleSaveToBucket = async () => {
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.SAVING_TO_BUCKET]);
    await actions('saveFavoriteToBucket', { id, adapter: AI_STUDIO_ADAPTERS.ACTIV8, projectId });
    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.SAVING_TO_BUCKET));
  };

  return (
    <div className={clsx(scss.card)}>
      {mediaUrl && mediaType === AI_MEDIA_TYPES.IMAGE && (
        <Image src={mediaUrl} alt={fileName} onClick={handleOpenModal} />
      )}
      {mediaUrl && mediaType === AI_MEDIA_TYPES.VIDEO && <Video src={mediaUrl} alt={fileName} />}
      {error && <FailedMedia />}

      <CardActions
        id={id}
        mediaType={mediaType}
        fileName={fileName}
        onDownload={handleDownload}
        onRemove={handleRemove}
        onSaveToBucket={isSaveToBucketAvailable ? handleSaveToBucket : undefined}
        onCreateNewBucket={handleCreateNewBucket}
        additionalCardActions={additionalCardActions}
        loadingState={loadingTypes}
      />

      <CommonLoader isLoading={isLoading} />
      <ImageModal isOpen={isOpen} onClose={handleCloseModal} src={mediaUrl} alt={fileName} />
      <ConfirmationModal
        isOpen={mediaToRemove}
        onClose={handleCloseConfirmationModal}
        onCancel={handleCloseConfirmationModal}
        onConfirm={handleRemoveMedia}
        subLabel={t('afterRemovingThisMediaWillBeDeletedFromYourProject')}
      />
    </div>
  );
}

export default MediaCard;

export const LOADING_TYPES = {
  CREATING_NEW_BUCKET: 'CREATING_NEW_BUCKET',
  REMOVING_MEDIA: 'REMOVING_MEDIA',
  SAVING_TO_BUCKET: 'SAVING_TO_BUCKET',
  DOWNLOADING_MEDIA: 'DOWNLOADING_MEDIA',
};
